/*
 * Cart drawer upsell - behaviour only.
 *
 * The carousel and its cards are rendered by snippets/cart-upsell.liquid, and
 * Shopify re-renders the whole drawer section on every cart change, so there
 * is nothing here watching the drawer: the original ran three MutationObservers
 * (one on the drawer's "open" attribute, one on its entire subtree, one on the
 * section's parent) plus a replacement for window.fetch that re-ran the whole
 * pipeline after any /cart/ request.
 *
 * What is left:
 *   - the carousel itself (arrows, dots, RTL-aware scrolling)
 *   - quick add, and the variant sheet for products with options
 *   - asking Variant's recommendation service for its picks and swapping the
 *     server-rendered cards for them when it answers
 *
 * Ported from the Variant experience exp-EXPAEEC24A5.
 */
(function () {
  'use strict';

  var EXPERIMENT_ID = 'EXPAEEC24A5';
  var VARIATION_ID = '1';

  var BRAIN_ENDPOINT = 'https://brain.variantnow.com/product-recommendations/similar';
  var SHOP_ID = 'f088554d-cd8f-49d8-8fe0-055b65127354';
  var PER_CART_ITEM = 15;
  var MAX_CARDS = 12;
  var CARD_GAP = 10;

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function track(productId) {
    try {
      if (typeof Variant === 'undefined' || !Variant.publishCustomEvent) return;
      Variant.publishCustomEvent('product-clicked-attribution', {
        experiment_id: EXPERIMENT_ID,
        variation_id: VARIATION_ID,
        product_id: productId
      });
    } catch (e) {
      /* never break the cart for analytics */
    }
  }

  function init(root) {
    if (root.dataset.cartUpsellReady) return;
    root.dataset.cartUpsellReady = '1';

    var track_ = root.querySelector('[data-cart-upsell-track]');
    var dots = root.querySelector('[data-cart-upsell-dots]');
    if (!track_) return;

    // -----------------------------------------------------------------------
    // Carousel
    //
    // This is an RTL drawer, so scrollLeft runs negative. Everything below
    // works off its absolute value and lets the sign fall out of the maths.
    // -----------------------------------------------------------------------
    function metrics() {
      var cards = track_.querySelectorAll('[data-cart-upsell-card]');
      if (!cards.length) return { cardWidth: 130, perView: 2, total: 0 };
      var cardWidth = cards[0].offsetWidth;
      return {
        cardWidth: cardWidth,
        perView: Math.max(1, Math.floor(track_.offsetWidth / (cardWidth + CARD_GAP))),
        total: cards.length
      };
    }

    function maxScroll() {
      return Math.abs(track_.scrollWidth - track_.offsetWidth);
    }

    function refreshArrows() {
      var position = Math.abs(track_.scrollLeft);
      var limit = maxScroll();
      root.querySelectorAll('[data-cart-upsell-step]').forEach(function (button) {
        var forwards = button.getAttribute('data-cart-upsell-step') === 'next';
        button.disabled = forwards ? position <= 2 : position >= limit - 2;
      });
    }

    function refreshDots() {
      if (!dots) return;

      var m = metrics();
      if (!m.total) return;

      var pages = Math.ceil(m.total / m.perView);
      refreshArrows();

      if (pages <= 1) {
        dots.replaceChildren();
        return;
      }

      var limit = maxScroll();
      var ratio = limit > 0 ? Math.abs(track_.scrollLeft) / limit : 0;
      var activePage = Math.round(ratio * (pages - 1));

      if (dots.children.length !== pages) {
        dots.replaceChildren();
        for (var page = 0; page < pages; page++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cart-upsell__dot';
          dot.setAttribute('data-page', String(page));
          dot.setAttribute('aria-label', String(page + 1));
          dots.appendChild(dot);
        }
      }

      Array.prototype.forEach.call(dots.children, function (dot, index) {
        dot.classList.toggle('is-active', index === activePage);
      });
    }

    function step() {
      var m = metrics();
      return m.perView * (m.cardWidth + CARD_GAP);
    }

    root.addEventListener('click', function (event) {
      var arrow = event.target.closest('[data-cart-upsell-step]');
      if (arrow) {
        var forwards = arrow.getAttribute('data-cart-upsell-step') === 'next';
        track_.scrollBy({ left: forwards ? -step() : step(), behavior: 'smooth' });
        return;
      }

      var dot = event.target.closest('.cart-upsell__dot');
      if (dot) {
        var m = metrics();
        var page = parseInt(dot.getAttribute('data-page'), 10);
        var pages = Math.ceil(m.total / m.perView);
        var target =
          page === pages - 1 ? maxScroll() : page * m.perView * (m.cardWidth + CARD_GAP);
        track_.scrollTo({ left: -target, behavior: 'smooth' });
        return;
      }

      var link = event.target.closest('.cart-upsell__media, .cart-upsell__title');
      if (link) {
        var linkCard = link.closest('[data-cart-upsell-card]');
        if (linkCard) track(linkCard.getAttribute('data-product-id'));
        return;
      }

      var addButton = event.target.closest('[data-cart-upsell-add]');
      if (addButton) {
        event.preventDefault();
        onAdd(addButton);
      }
    });

    var scrollTimer = null;
    track_.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(refreshDots, 80);
      },
      { passive: true }
    );

    refreshDots();

    // -----------------------------------------------------------------------
    // Adding
    // -----------------------------------------------------------------------
    function onAdd(button) {
      var card = button.closest('[data-cart-upsell-card]');
      if (!card) return;

      if (button.getAttribute('data-has-options') === 'true') {
        openVariantSheet(card);
        return;
      }

      addToCart(button.getAttribute('data-variant-id'), card.getAttribute('data-product-id'), button);
    }

    function addToCart(variantId, productId, button) {
      if (!variantId) return;
      track(productId);

      if (button) button.disabled = true;

      var sections = ['cart-drawer', 'header']
        .map(function (name) {
          var element = document.querySelector('.shopify-section[id*="' + name + '"]');
          return element ? element.id.replace('shopify-section-', '') : null;
        })
        .filter(Boolean);

      var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          if (!json || json.status || !json.sections) return;

          // Re-rendering the drawer brings back a fresh carousel with the new
          // cart taken into account, so nothing here has to update by hand.
          Object.keys(json.sections).forEach(function (id) {
            var html = json.sections[id];
            if (html == null) return;
            var element = document.getElementById('shopify-section-' + id);
            if (!element) return;
            element.outerHTML = html;
            document.dispatchEvent(
              new CustomEvent('shopify:section:load', { detail: { sectionId: id } })
            );
          });
        })
        .catch(function () {
          /* leave the drawer as it is */
        })
        .then(function () {
          if (button) button.disabled = false;
        });
    }

    // -----------------------------------------------------------------------
    // Variant sheet
    // -----------------------------------------------------------------------
    function variantDataFor(card) {
      var payload = card.querySelector('[data-cart-upsell-variants]');
      if (!payload) return null;
      try {
        return JSON.parse(payload.textContent);
      } catch (e) {
        return null;
      }
    }

    function openVariantSheet(card) {
      var data = variantDataFor(card);
      if (!data) {
        // A card swapped in from the recommendation service carries no variant
        // data, so fetch it the one time someone asks for it.
        loadVariantData(card).then(function (loaded) {
          if (loaded) openVariantSheet(card);
        });
        return;
      }

      var existing = document.querySelector('.cart-upsell-sheet');
      if (existing) existing.remove();

      var selections = {};
      var firstAvailable = data.variants.filter(function (variant) {
        return variant.available;
      })[0];
      if (firstAvailable) {
        data.options.forEach(function (option, index) {
          selections[option.name] = firstAvailable.options[index];
        });
      }

      var overlay = document.createElement('div');
      overlay.className = 'cart-upsell-sheet';
      overlay.innerHTML =
        '<div class="cart-upsell-sheet__panel">' +
        '<div class="cart-upsell-sheet__grip"><span></span></div>' +
        '<button type="button" class="cart-upsell-sheet__close" aria-label="close">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="cart-upsell-sheet__head">' +
        '<img class="cart-upsell-sheet__image" src="" alt="">' +
        '<div class="cart-upsell-sheet__meta">' +
        '<div class="cart-upsell-sheet__title"></div>' +
        '<div class="cart-upsell-sheet__price"></div>' +
        '</div>' +
        '</div>' +
        '<div class="cart-upsell-sheet__options"></div>' +
        '<div class="cart-upsell-sheet__actions">' +
        '<button type="button" class="cart-upsell-sheet__submit" disabled>הוספה לסל</button>' +
        '</div>' +
        '</div>';

      // Kept inside the drawer so moving focus into it never reads as the
      // drawer losing focus, which would close it.
      var host = document.querySelector('cart-drawer, #cart-drawer') || document.body;
      host.appendChild(overlay);

      var image = overlay.querySelector('.cart-upsell-sheet__image');
      image.src = data.image || '';
      image.alt = data.title || '';
      overlay.querySelector('.cart-upsell-sheet__title').textContent = data.title || '';

      var optionsHost = overlay.querySelector('.cart-upsell-sheet__options');
      var priceEl = overlay.querySelector('.cart-upsell-sheet__price');
      var submit = overlay.querySelector('.cart-upsell-sheet__submit');

      function matchedVariant() {
        return data.variants.filter(function (variant) {
          return data.options.every(function (option, index) {
            return selections[option.name] === variant.options[index];
          });
        })[0];
      }

      function render() {
        optionsHost.replaceChildren();

        data.options.forEach(function (option, optionIndex) {
          var group = document.createElement('div');
          group.className = 'cart-upsell-sheet__group';

          var label = document.createElement('span');
          label.className = 'cart-upsell-sheet__label';
          label.textContent = option.name + ':';
          group.appendChild(label);

          var values = document.createElement('div');
          values.className = 'cart-upsell-sheet__values';

          option.values.forEach(function (value) {
            // A value is offered when some purchasable variant carries it
            // alongside everything else already picked.
            var reachable = data.variants.some(function (variant) {
              if (!variant.available) return false;
              if (variant.options[optionIndex] !== value) return false;
              return data.options.every(function (other, otherIndex) {
                if (otherIndex === optionIndex) return true;
                if (!selections[other.name]) return true;
                return variant.options[otherIndex] === selections[other.name];
              });
            });

            var swatch = document.createElement('button');
            swatch.type = 'button';
            swatch.className = 'cart-upsell-sheet__swatch';
            swatch.textContent = value;
            swatch.disabled = !reachable;
            swatch.classList.toggle('is-selected', selections[option.name] === value);
            swatch.classList.toggle('is-unavailable', !reachable);
            swatch.addEventListener('click', function () {
              selections[option.name] = value;
              render();
            });

            values.appendChild(swatch);
          });

          group.appendChild(values);
          optionsHost.appendChild(group);
        });

        var variant = matchedVariant();
        if (!variant) {
          priceEl.textContent = '';
          submit.disabled = true;
          submit.textContent = 'לא זמין';
          return;
        }

        priceEl.replaceChildren();
        var price = document.createElement('span');
        price.textContent = variant.price + ' ₪';
        if (variant.compareAtPrice) price.className = 'cart-upsell-sheet__price--sale';
        priceEl.appendChild(price);

        if (variant.compareAtPrice) {
          var was = document.createElement('span');
          was.className = 'cart-upsell-sheet__price-compare';
          was.textContent = variant.compareAtPrice + ' ₪';
          priceEl.appendChild(was);
        }

        submit.disabled = !variant.available;
        submit.textContent = variant.available ? 'הוספה לסל' : 'אזל';
      }

      render();

      submit.addEventListener('click', function () {
        var variant = matchedVariant();
        if (!variant || !variant.available) return;
        close();
        addToCart(variant.id, card.getAttribute('data-product-id'), null);
      });

      function close() {
        overlay.classList.remove('is-open');
        setTimeout(function () {
          overlay.remove();
        }, 300);
      }

      overlay.querySelector('.cart-upsell-sheet__close').addEventListener('click', function (event) {
        event.stopPropagation();
        close();
      });

      // The drawer closes on outside clicks, so nothing here may reach it.
      ['click', 'mousedown', 'touchstart'].forEach(function (type) {
        overlay.addEventListener(type, function (event) {
          event.stopPropagation();
          if (type === 'click' && event.target === overlay) close();
        });
      });

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          overlay.classList.add('is-open');
        });
      });
    }

    /** Same-origin product JSON - no Storefront token, and only on demand. */
    function loadVariantData(card) {
      var handle = card.getAttribute('data-product-handle');
      if (!handle) return Promise.resolve(false);

      return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (product) {
          var payload = {
            title: product.title,
            image: product.featured_image || '',
            options: (product.options || []).map(function (option) {
              return { name: option.name, values: option.values };
            }),
            variants: (product.variants || []).map(function (variant) {
              return {
                id: variant.id,
                available: variant.available,
                price: (variant.price / 100).toFixed(2),
                compareAtPrice:
                  variant.compare_at_price && variant.compare_at_price > variant.price
                    ? (variant.compare_at_price / 100).toFixed(2)
                    : null,
                options: variant.options
              };
            })
          };

          var script = document.createElement('script');
          script.type = 'application/json';
          script.setAttribute('data-cart-upsell-variants', '');
          script.textContent = JSON.stringify(payload);
          card.appendChild(script);
          return true;
        })
        .catch(function () {
          return false;
        });
    }

    // -----------------------------------------------------------------------
    // Variant's own recommendations
    // -----------------------------------------------------------------------
    applyRecommendations(root, track_, refreshDots);
  }

  /**
   * Ask the recommendation service for picks based on what is in the cart and,
   * if it answers with anything usable, replace the server-rendered cards.
   * Failure is not handled because there is nothing to handle: what Liquid
   * already rendered stays on screen.
   */
  function applyRecommendations(root, track_, onDone) {
    var cartHandles = Array.prototype.map.call(
      document.querySelectorAll('[data-cart-upsell-cart-handle]'),
      function (node) {
        return node.getAttribute('data-cart-upsell-cart-handle');
      }
    );

    if (!cartHandles.length) return;

    Promise.all(
      cartHandles.slice(0, 4).map(function (handle) {
        var url =
          BRAIN_ENDPOINT +
          '?shop_id=' +
          encodeURIComponent(SHOP_ID) +
          '&handle=' +
          encodeURIComponent(handle) +
          '&limit=' +
          PER_CART_ITEM;

        return fetch(url)
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (json) {
            if (!json || !json.success) return [];
            return json.similar_products || [];
          })
          .catch(function () {
            return [];
          });
      })
    ).then(function (perItem) {
      var seen = {};
      cartHandles.forEach(function (handle) {
        seen[handle] = true;
      });

      // Round-robin so every item in the cart contributes, as the original did:
      // one pick from each list per pass, each list keeping its own place.
      var picked = [];
      var cursors = perItem.map(function () {
        return 0;
      });
      var moved = true;

      while (moved && picked.length < MAX_CARDS) {
        moved = false;

        for (var list = 0; list < perItem.length && picked.length < MAX_CARDS; list++) {
          while (cursors[list] < perItem[list].length) {
            var candidate = perItem[list][cursors[list]];
            cursors[list]++;
            if (seen[candidate.handle]) continue;

            seen[candidate.handle] = true;
            picked.push(candidate);
            moved = true;
            break;
          }
        }
      }

      if (!picked.length) return;

      track_.replaceChildren();
      picked.forEach(function (product) {
        track_.appendChild(buildCard(product));
      });

      onDone();
    });
  }

  function buildCard(product) {
    var card = document.createElement('div');
    card.className = 'cart-upsell__card';
    card.setAttribute('data-cart-upsell-card', '');
    card.setAttribute('data-product-id', product.product_id || '');
    card.setAttribute('data-product-handle', product.handle || '');

    var url = '/products/' + product.handle;
    var price = parseFloat(product.price);
    var compareAt = parseFloat(product.compare_at_price);
    var onSale = compareAt > price;

    var mediaWrap = document.createElement('div');
    mediaWrap.className = 'cart-upsell__media-wrap';
    var mediaLink = document.createElement('a');
    mediaLink.className = 'cart-upsell__media';
    mediaLink.href = url;
    mediaLink.draggable = false;
    if (product.featured_image_url) {
      var image = document.createElement('img');
      image.className = 'cart-upsell__image';
      image.src = product.featured_image_url;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.draggable = false;
      mediaLink.appendChild(image);
    }
    mediaWrap.appendChild(mediaLink);

    var info = document.createElement('div');
    info.className = 'cart-upsell__info';

    var titleLink = document.createElement('a');
    titleLink.className = 'cart-upsell__title';
    titleLink.href = url;
    titleLink.draggable = false;
    titleLink.textContent = product.title || '';
    info.appendChild(titleLink);

    var prices = document.createElement('div');
    prices.className = 'cart-upsell__prices';
    var priceEl = document.createElement('span');
    priceEl.className = onSale ? 'cart-upsell__price cart-upsell__price--sale' : 'cart-upsell__price';
    priceEl.textContent = (isNaN(price) ? '' : price.toFixed(2)) + ' ₪';
    prices.appendChild(priceEl);
    if (onSale) {
      var compareEl = document.createElement('span');
      compareEl.className = 'cart-upsell__price-compare';
      compareEl.textContent = compareAt.toFixed(2) + ' ₪';
      prices.appendChild(compareEl);
    }
    info.appendChild(prices);

    // The variant sheet resolves options lazily for these, so every card can
    // safely route through it.
    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'cart-upsell__add';
    add.setAttribute('data-cart-upsell-add', '');
    add.setAttribute('data-has-options', 'true');
    add.innerHTML =
      '<svg aria-hidden="true" focusable="false" fill="none" width="10" viewBox="0 0 12 12">' +
      '<path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"></path></svg><span>הוספה לסל</span>';
    info.appendChild(add);

    card.appendChild(mediaWrap);
    card.appendChild(info);
    return card;
  }

  function boot() {
    var root = document.querySelector('[data-cart-upsell]');
    if (root) init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify swaps the drawer section on every cart change.
  document.addEventListener('shopify:section:load', boot);
})();
